"use client";

import { useState, type FormEvent, type ReactNode } from "react";
import {
  Globe,
  Phone,
  Mail,
  MapPin,
  ClipboardCheck,
  ShieldCheck,
  Scale,
  Lock,
  Clock,
  ChevronDown,
  Paperclip,
  CheckCircle2,
} from "lucide-react";

const contactMethods = [
  { icon: Globe, title: "Trực tuyến", desc: "Bạn có thể nhắn trực tiếp ở chatbox bên tay phải bao gồm zalo và hotline.", cta: "Gửi phản ánh trực tuyến", href: "" },
  { icon: Phone, title: "Điện thoại", desc: "Liên hệ trực tiếp với bộ phận chăm sóc khách hàng qua hotline.", cta: "Gọi ngay", value: "0778 999 494", href: "tel:0778999494" },
  { icon: Mail, title: "Email", desc: "Gửi email mô tả chi tiết vấn đề để được hỗ trợ và phản hồi.", cta: "Gửi email", value: "nguyenthanhloi1405@gmail.com", href: "mailto:nguyenthanhloi1405@gmail.com" },
  { icon: MapPin, title: "Trực tiếp", desc: "Ghé cửa hàng Guitar Sài Thành trong giờ làm việc để được hỗ trợ trực tiếp.", cta: "Xem địa chỉ", value: "35 Thạnh Xuân 31, P. Thới An, Q.12, TP.HCM", href: "#" },
];

const requestTypes = ["Phản ánh", "Yêu cầu hỗ trợ", "Khiếu nại", "Bảo hành", "Đổi / trả sản phẩm", "Giao hàng", "Thanh toán", "Khác"];

type FormErrors = Partial<Record<"name" | "phone" | "email" | "type" | "subject" | "message" | "consent", string>>;

export default function ReflectionFormSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  function validate(formData: FormData): FormErrors {
    const next: FormErrors = {};
    const name = String(formData.get("name") || "").trim();
    const phone = String(formData.get("phone") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const type = String(formData.get("type") || "");
    const subject = String(formData.get("subject") || "").trim();
    const message = String(formData.get("message") || "").trim();
    const consent = formData.get("consent");

    if (!name) next.name = "Vui lòng nhập họ và tên.";
    if (!phone) next.phone = "Vui lòng nhập số điện thoại.";
    else if (!/^(0|\+84)\d{9,10}$/.test(phone.replace(/\s/g, "")))
      next.phone = "Số điện thoại chưa đúng định dạng.";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      next.email = "Email chưa đúng định dạng.";
    if (!type) next.type = "Vui lòng chọn loại yêu cầu.";
    if (!subject) next.subject = "Vui lòng nhập tiêu đề.";
    if (!message) next.message = "Vui lòng mô tả nội dung chi tiết.";
    if (!consent) next.consent = "Vui lòng xác nhận thông tin để gửi yêu cầu.";

    return next;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    const formData = new FormData(e.currentTarget);
    const validation = validate(formData);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;

    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 900);
  }

  return (
    <main className="mx-auto max-w-5xl mt-20 px-4 py-16 sm:px-6 lg:px-8">
      <section className="mb-16 ">
        <p className="mb-2 text-sm font-medium uppercase tracking-wide text-amber-700">
          Chính sách hỗ trợ khách hàng
        </p>
        <h1 className="mb-4 text-3xl font-bold sm:text-4xl">
          Phương thức tiếp nhận và giải quyết phản ánh, yêu cầu, khiếu nại
        </h1>
        <p className="max-w-3xl text-base leading-relaxed text-neutral-600">
          Guitar Sài Thành luôn lắng nghe ý kiến của khách hàng và cam kết tiếp
          nhận, xử lý phản ánh, yêu cầu và khiếu nại một cách minh bạch, khách
          quan và thiện chí.
        </p>
      </section>

      <section className="mb-16">
        <h2 className="mb-6 text-2xl font-semibold">Các phương thức tiếp nhận</h2>
        <div className="grid gap-5 sm:grid-cols-2">
          {contactMethods.map(({ icon: Icon, title, desc, cta, value, href }) => (
            <div key={title} className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                <Icon size={20} />
              </div>
              <h3 className="mb-1 font-semibold">{title}</h3>
              <p className="mb-3 text-sm text-neutral-600">{desc}</p>
              {value && <p className="mb-3 text-sm font-medium">{value}</p>}
              <a href={href} className="text-sm font-medium text-amber-700 hover:underline">
                {cta} →
              </a>
            </div>
          ))}
        </div>
      </section>


    </main>
  );
}

function inputClass(hasError: boolean) {
  return `w-full rounded-lg border px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-amber-600/40 ${
    hasError ? "border-red-400" : "border-neutral-300"
  }`;
}

function Field({
  label,
  required,
  error,
  className = "",
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}